/**
 * TypeScript mirror of tokens.css for values the MUI theme (and inline sx)
 * needs as raw strings. tokens.css is the source of truth — keep in sync.
 *
 * tokens.unit.ts asserts the two files agree on the values that have drifted
 * before (fog-80, success-10) so a future re-port cannot regress them silently.
 */

export const colors = {
  pink: {
    10: '#fff0f7',
    20: '#fbdbea',
    40: '#f9c9e0',
    60: '#f292c0',
    80: '#e84895',
    100: '#6d0d3c',
  },
  black: {
    0: '#ffffff',
    10: '#fdfdfd',
    20: '#f7f7f7',
    30: '#dfdfdf',
    40: '#c7c7cd',
    60: '#686f78',
    80: '#464646',
    100: '#313131',
  },
  purple: {
    10: '#fbf2ff',
    20: '#f1e2f9',
    40: '#ddc6e9',
    60: '#b497c3',
    80: '#5960a4',
    100: '#3f4582',
  },
  fog: {
    10: '#f3f4f8',
    20: '#e6e8ef',
    40: '#c6c9e2',
    60: '#aab3d1',
    80: '#848dae',
    100: '#434a66',
  },
  blue: {
    10: '#ebf0ff',
    20: '#d7e1ff',
    40: '#b4c6ff',
    60: '#7f9efe',
    80: '#4976fe',
    100: '#0d41a9',
  },
  maroon: {
    10: '#fff4f4',
    20: '#f1dede',
    40: '#dfc7c7',
    60: '#d7a4a4',
    80: '#be6565',
    100: '#562524',
  },
  coral: {
    10: '#fff6f6',
    20: '#ffe4e4',
    40: '#ffc1c1',
    60: '#ff8d90',
    80: '#ff5a5f',
    100: '#ad2b2f',
  },
  /** Only 10/100 exist in the design system. */
  navy: { 10: '#ebf0ff', 100: '#1a1a70' },
  warning: { 10: '#fffbed', 80: '#fcb100', 100: '#562524' },
  success: { 10: '#edfff3', 80: '#54ba25', 100: '#2b7d3d' },
  error: { 10: '#fff8f8', 80: '#ff5a5f', 100: '#ae2b30' },
  info: { 10: '#f6f8ff', 80: '#4976fe', 100: '#0042a4' },
} as const;

export const semantic = {
  fg1: colors.black[100],
  fg2: colors.black[80],
  fg3: colors.black[60],
  fgDisabled: colors.black[40],
  fgBrand: colors.pink[80],
  fgHeading: colors.navy[100],
  bgApp: colors.black[20],
  bgPanel: colors.black[10],
  bgSunken: colors.fog[10],
  bgHover: colors.fog[20],
  bgActive: colors.fog[40],
  borderSubtle: colors.black[40],
  borderDivider: colors.black[30],
} as const;

/** Figma's token is `Geist`; @fontsource/geist-sans registers `Geist Sans`. */
export const fontFamily = "'Geist', 'Geist Sans', Arial, Verdana, sans-serif";
export const fontFamilyMono = "'Geist Mono', ui-monospace, monospace";

/**
 * The design system's type scale (Figma 4278:259). Nothing in the app may use a
 * size outside this list — reach for a theme typography variant first, these only
 * second.
 *
 * There is no 12 and no 13. `Label` is **11px** and the Geist Mono label is
 * **10px**; Web's `label: 12px` is a Web-only value that this repo briefly
 * adopted, which inflated every dense surface in the plan tree and the panels.
 */
export const fontSize = {
  labelMono: 10,
  label: 11,
  body: 14,
  h4: 16,
  h3: 20,
  /** Display2 shares H3's size but is semibold. */
  display2: 20,
  h2: 24,
  h1: 32,
  display1: 40,
} as const;

export const fontWeight = {
  light: 200,
  regular: 400,
  semibold: 500,
  bold: 600,
} as const;

export const radius = {
  small: 4,
  medium: 6,
  large: 8,
  xLarge: 12,
  pill: 100,
} as const;

/** Control heights — matches Figma's `Primitives/Button size` in Design System v1. */
export const inputHeight = {
  tiny: 24,
  small: 32,
  medium: 40,
  large: 48,
} as const;

export const borderWidth = {
  hairline: '0.5px',
  thin: '1px',
  thick: '1.5px',
} as const;

/** PM-local layout dimensions. Structural, not spacing — never snap these to the 8pt grid. */
export const layout = {
  sidebarWidth: 250,
  topNavigationHeight: 48,
  pageWrapperPadding: 10,
  contentMaxWidth: 1100,
  proseMaxWidth: 400,
  railWidth: 340,
  contractsRailOpen: 200,
  contractsRailCollapsed: 48,
} as const;

export const zIndex = {
  stickyHeader: 2,
  rowAffordance: 6,
  /** Drag marks inside the timeline's scroller — above the sticky name column. */
  dragOverlay: 8,
  dropIndicator: 90,
  floatingBar: 95,
} as const;
